import styled from 'styled-components';
import { useBannerContext } from '../context/banner-context';
import { BannerHelpers } from '../utils/use-banner-helpers';
import { EnhancedCampaign } from '~/components/casino-shared/types';
import breakpoint from '~/utils/style-utils';

const getTemplateAreasMobile = (data: EnhancedCampaign, helpers: BannerHelpers) => {
	if (helpers.isHorizontalOnMobile) {
		if (helpers.isSingleItem) {
			return 'center';
		}

		if (helpers.isAnyTextVisible ? helpers.isTextCentered || helpers.isAllImagesEnabled : false) {
			return 'left center right';
		}

		if (helpers.columnItemsCount === 2) {
			return 'left right';
		}

		if (helpers.isAnyTextVisible) {
			return data.textPosition.toLowerCase();
		}

		return data.foregroundImage1Position?.toLowerCase() || 'left';
	}

	if (helpers.isAllImagesEnabled) {
		return ['top-left top-right', 'bottom bottom'].join("' '");
	}

	// for mobile, it can only be top and bottom according to the number of items in the vertical layout
	return ['top', 'bottom'].slice(0, helpers.columnItemsCount).join("' '");
};

const getTemplateAreasDesktop = (data: EnhancedCampaign, helpers: BannerHelpers) => {
	if (helpers.isSingleItem) {
		return 'center';
	}

	if (helpers.isAnyTextVisible ? helpers.isTextCentered || helpers.isAllImagesEnabled : false) {
		return 'left center right';
	}

	if (helpers.columnItemsCount === 2) {
		return 'left right';
	}

	if (helpers.isAnyTextVisible) {
		return data.textPosition.toLowerCase();
	}

	return data.foregroundImage1Position?.toLowerCase() || 'left';
};

const getMobileJustifyContent = (data: EnhancedCampaign, helpers: BannerHelpers) => {
	return data.mobileLayoutType === 'VERTICAL' || helpers.isSingleItem ? 'center' : 'space-between';
};

const getDesktopJustifyContent = (data: EnhancedCampaign, helpers: BannerHelpers) => {
	if (helpers.isSingleItem) {
		return 'center';
	}
	return data.desktopLayoutType === 'PACKED' ? 'center' : 'space-between';
};

const getTemplateColumnsMobile = (helpers: BannerHelpers) => {
	if (helpers.isHorizontalOnMobile) {
		if (helpers.isTextCentered && !helpers.isSingleItem) {
			return 'repeat(3, 1fr)';
		}
		return `repeat(${helpers.columnItemsCount}, minmax(30%, 452px))`;
	}

	if (helpers.isAllImagesEnabled) {
		return '1fr 1fr';
	}
	return '1fr';
};

const getTemplateColumnsDesktop = (helpers: BannerHelpers) => {
	if (helpers.isTextCentered && !helpers.isSingleItem) {
		return 'repeat(3, minmax(30%, 452px))';
	}
	return `repeat(${helpers.columnItemsCount}, minmax(30%, 452px))`;
};

const getTemplateRowsMobile = (data: EnhancedCampaign) => {
	if (data.mobileLayoutType === 'VERTICAL') {
		return `repeat(auto-fit, minmax(60px, 1fr))`;
	}

	// for mobile layout short and horizontal
	return '1fr';
};

const getTemplateRowsDesktop = () => {
	return '1fr';
};

const StyledGrid = styled.div<{ bannerData: EnhancedCampaign; helpers: BannerHelpers }>`
	padding: 12px;
	display: grid;
	grid-area: banner;
	align-content: start;
	align-items: center;
	justify-content: ${({ bannerData, helpers }) => getMobileJustifyContent(bannerData, helpers)};
	justify-items: center;
	grid-template-areas: ${({ bannerData, helpers }) => `'${getTemplateAreasMobile(bannerData, helpers)}'`};
	grid-gap: 12px;
	grid-template-columns: ${({ helpers }) => getTemplateColumnsMobile(helpers)};
	grid-template-rows: ${({ bannerData }) => getTemplateRowsMobile(bannerData)};
	overflow: hidden;

	${({ bannerData, helpers }) => breakpoint.lg`
		padding: 12px 60px;
		grid-gap: 48px;
		justify-content: ${getDesktopJustifyContent(bannerData, helpers)};
		grid-template-columns: ${getTemplateColumnsDesktop(helpers)};
		grid-template-rows: ${getTemplateRowsDesktop()};
		grid-template-areas: "${getTemplateAreasDesktop(bannerData, helpers)}";
	`}
`;

const ContentGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { data, helpers } = useBannerContext();

	return (
		<StyledGrid bannerData={data} helpers={helpers}>
			{children}
		</StyledGrid>
	);
};

export default ContentGrid;
